import { z } from "zod";
import { FlowLintSummarySchema } from "./flow-lint.js";
import {
  AlarmStateSchema,
  ConcurrencyMetricSchema,
  EventTypeSchema,
  ContactRecordPayloadSchema,
  JourneyDimensionSchema,
  JourneyEdgeSchema,
  QuotaScopeSchema,
  QuotaUsageSourceSchema,
  SeveritySchema,
  TemplateComponentSchema,
  CrossAccountSchema,
} from "./event-envelope.js";

/**
 * DynamoDB record shapes for the central account (CLAUDE.md section 13,
 * ADR-0001, ADR-0009). An organization is the company; an environment is
 * one AWS account running one deployment of the customer template.
 * Tables holding data emitted by an account partition on
 * `environment_id`; configuration that spans environments, and the audit
 * trail, partition on `organisation_id`. Both are only ever resolved
 * server-side from the API key or the JWT - never from a request body.
 */

/** ADR-0009: the company. Holds ADR-0008's ordered environment chain. */
export const OrganisationRecordSchema = z.object({
  organisation_id: z.string().min(1),
  name: z.string().min(1),
  status: z.enum(["active", "suspended", "terminated"]),
  /**
   * ADR-0008's chain, ordered lowest first. An array rather than a
   * position per environment so reordering is one atomic write and two
   * environments cannot claim the same slot.
   */
  environment_chain: z.array(z.string().min(1)),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
export type OrganisationRecord = z.infer<typeof OrganisationRecordSchema>;

/**
 * An organization's production environment: the last in its ADR-0008 chain (the owner's definition of
 * 2026-09-27, for Focus's Yesterday row). Not `live`, which is declared per environment and is true of a
 * development instance that carries real traffic. Undefined for an organization with no chain yet.
 */
export function productionEnvironmentId(chain: readonly string[]): string | undefined {
  return chain.length > 0 ? chain[chain.length - 1] : undefined;
}

/**
 * `Memberships` - PK `organisation_id`, SK `user_id#environment_id`.
 * Deliberately not a token claim (ADR-0009): a token is a cache, and
 * revoking someone's production access has to bite on the next request
 * rather than at the next refresh.
 */
export const MembershipRecordSchema = z.object({
  organisation_id: z.string().min(1),
  sk: z.string().min(1),
  user_id: z.string().min(1),
  environment_id: z.string().min(1),
  /** ADR-0017: the roles held on this environment - built-in ids (`viewer`, `admin`, `manager`) or the organization's own. */
  role_ids: z.array(z.string().min(1)).optional(),
  /** ADR-0017 section 2b: which profiles this person may see here - all (the default) or a chosen set. */
  all_profiles: z.boolean().optional(),
  profile_ids: z.array(z.string().min(1)).optional(),
  granted_by: z.string().min(1),
  granted_at: z.string().datetime({ offset: true }),
});
export type MembershipRecord = z.infer<typeof MembershipRecordSchema>;

/** The roles a membership holds (ADR-0017). The pre-ADR-0017 `role` is no longer read (#241); `admin/migrate-memberships.ts` moves any row still on it. */
export function membershipRoleIds(m: Pick<MembershipRecord, "role_ids">): string[] {
  return m.role_ids ?? [];
}

/**
 * `Roles` - PK `organisation_id`, SK `role_id` (ADR-0017). Built-ins live
 * under the `__builtin__` sentinel and are read by every organization.
 */
export const RoleRecordSchema = z.object({
  organisation_id: z.string().min(1),
  role_id: z.string().min(1),
  name: z.string().min(1).max(60),
  description: z.string().max(300).optional(),
  permissions: z.array(z.string().min(1)),
  built_in: z.boolean(),
  created_by: z.string().min(1),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
export type RoleRecord = z.infer<typeof RoleRecordSchema>;

/** `Groups` - PK `organisation_id`, SK `group_id`: a named set of roles, its members, and the environments the roles apply on. */
export const GroupRecordSchema = z.object({
  organisation_id: z.string().min(1),
  group_id: z.string().min(1),
  name: z.string().min(1).max(60),
  role_ids: z.array(z.string().min(1)),
  member_user_ids: z.array(z.string().min(1)),
  environment_ids: z.array(z.string().min(1)),
  created_by: z.string().min(1),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
export type GroupRecord = z.infer<typeof GroupRecordSchema>;

export const EnvironmentStatusSchema = z.enum(["active", "suspended", "terminated"]);
export type EnvironmentStatus = z.infer<typeof EnvironmentStatusSchema>;

export const OnboardingStatusSchema = z.enum(["pending", "active"]);
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;

/**
 * #397: what offboarding has handed over. `export` names the object in the
 * central exports bucket - never the link, which is a bearer credential and
 * lives only in the letter. `--purge-data` refuses until one of `export` or
 * `export_waived_at` is set.
 */
export const OffboardingStateSchema = z.object({
  letter_written_at: z.string().datetime({ offset: true }).optional(),
  export: z
    .object({
      produced_at: z.string().datetime({ offset: true }),
      bucket: z.string().min(1),
      key: z.string().min(1),
      bytes: z.number().int().nonnegative(),
      rows: z.record(z.string(), z.number().int().nonnegative()),
    })
    .optional(),
  export_waived_at: z.string().datetime({ offset: true }).optional(),
  export_waived_by: z.string().optional(),
});
export type OffboardingState = z.infer<typeof OffboardingStateSchema>;

/** ADR-0005: the ticketing destination is per-customer config, never code or envelope. */
export const EventDestinationSchema = z.enum(["connect_cases", "none"]);
export type EventDestination = z.infer<typeof EventDestinationSchema>;

export const EnvironmentRecordSchema = z.object({
  environment_id: z.string().min(1),
  /** ADR-0009: the organization this environment belongs to. */
  organisation_id: z.string().min(1),
  friendly_name: z.string().min(1),
  aws_account_number: z.string().regex(/^\d{12}$/),
  /** API Gateway API key ID (not the value). Indexed by `api_key_id-index`. */
  api_key_id: z.string().min(1),
  /** Secrets Manager ARN holding the key value, for handoff and rotation. Never the value itself. */
  api_key_secret_arn: z.string().min(1),
  onboarding_status: OnboardingStatusSchema,
  status: EnvironmentStatusSchema,
  event_destination: EventDestinationSchema,
  destination_config: z
    .object({
      /** Connect Cases requires a Customer Profiles ARN as the case's `customer_id` field. */
      connect_customer_profile_arn: z.string().optional(),
    })
    .optional(),
  connectivity_verified_at: z.string().datetime({ offset: true }).optional(),
  last_heartbeat_at: z.string().datetime({ offset: true }).optional(),
  /** Set by the quiet-account watcher when the heartbeat has been missing long enough to raise a case; cleared when it returns. */
  quiet_since: z.string().datetime({ offset: true }).optional(),
  /**
   * Key rotation (runbooks/key-rotation-runbook.md): the key being retired stays resolvable through
   * `previous_api_key_id-index` until the new one has heartbeated, so the customer's stack update is not a gap.
   */
  previous_api_key_id: z.string().optional(),
  previous_api_key_secret_arn: z.string().optional(),
  key_rotated_at: z.string().datetime({ offset: true }).optional(),
  /** Which key the last heartbeat arrived on - what tells a rotation it is safe to retire the old one. */
  last_heartbeat_api_key_id: z.string().optional(),
  /**
   * Set by offboarding's `--revoke-key` (#366): the key named by `api_key_id` no longer exists at API Gateway. The id
   * stays for history - the audit trail names it - but nothing may resolve an environment through it again.
   */
  api_key_revoked_at: z.string().datetime({ offset: true }).optional(),
  /** Set by offboarding when the Customer Profile is removed from the Cases account; the ARN stays for history. */
  customer_profile_deleted_at: z.string().datetime({ offset: true }).optional(),
  /** #397: when --terminate first set `terminated`; the 30-day purge grace counts from here. Older terminations read it from AuditLog. */
  terminated_at: z.string().datetime({ offset: true }).optional(),
  /** #397: the handover - the letter, and the export of this environment's central data or its explicit waiver. */
  offboarding: OffboardingStateSchema.optional(),
  template_version: z.string().optional(),
  stack_id: z.string().optional(),
  region: z.string().optional(),
  enabled_components: z.array(TemplateComponentSchema).optional(),
  forwarder_dlq_depth: z.number().int().nonnegative().optional(),
  /** ADR-0017 section 4: staff subjects who may not open this environment at all; set by a Gairm superadmin. */
  excluded_staff: z.array(z.string().min(1)).optional(),
  /** Set by the router from the pollers: false when the support plan lacks the API (requirements.md section 5). */
  trusted_advisor_available: z.boolean().optional(),
  support_api_available: z.boolean().optional(),
  /**
   * ADR-0023: when the stack's support case poller last finished a check - the run's start, recorded from its
   * last chunk or its sentinel. "Last checked" on the page; only ever moves forward.
   */
  support_cases_checked_at: z.string().datetime({ offset: true }).optional(),
  /** ADR-0023: when the portal last asked the stack to check now - the per-environment throttle. */
  support_refresh_requested_at: z.string().datetime({ offset: true }).optional(),
  /**
   * ADR-0008. `live` is declared, never inferred from chain position - a
   * customer may order a sandbox above production, and inferring it from
   * "last in the chain" would gate the wrong environment.
   */
  live: z.boolean(),
  changes_require_approval: z.boolean(),
  /**
   * ADR-0008 break glass: an admin may push a gated change through
   * without approval where the customer has turned this on. Off by
   * default, because some customers will want the hard stop.
   */
  break_glass_enabled: z.boolean().optional(),
  /**
   * Which configuration version this environment's account has actually
   * pulled, per profile (requirements.md 10.5). Recorded on the pull, so
   * the portal can show the gap the way it shows template drift.
   */
  config_synced_versions: z.record(z.string(), z.number().int().nonnegative()).optional(),
  config_synced_at: z.string().datetime({ offset: true }).optional(),
  journey_config: z
    .object({
      contact_flow_log_group: z.string().optional(),
      contact_flow_log_group_fallback: z.string().optional(),
      log_groups: z.array(z.string()).optional(),
      log_group_discovery: z.boolean().optional(),
      connect_instance_id: z.string().optional(),
      cache_retention_hours: z.number().int().positive().optional(),
    })
    .optional(),
  /**
   * Reported by the heartbeat: the role ARNs and cache table this stack
   * created for its opt-ins (#93). The router only stores ARNs in the
   * environment's own account, so an assumed role is always one the
   * customer's stack declared.
   */
  cross_account: CrossAccountSchema.optional(),
  /** #352: which flows the customer-side lint flagged, by name - set from the inventory, never from the portal. */
  flow_lint: FlowLintSummarySchema.optional(),
  /** #564: the template version this environment was last told is available, so the heartbeat tells it once per version. */
  template_notice_version: z.string().optional(),
  /** #564: per profile, the configuration version the stack was last told it could not pull, so config sync tells it once per version. */
  config_unpulled_notified: z.record(z.string(), z.number().int().nonnegative()).optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
export type EnvironmentRecord = z.infer<typeof EnvironmentRecordSchema>;

/** `Events` item. SK is `${timestamp}#${event_id}` so a partition reads in time order. */
export const EventRecordSchema = z.object({
  environment_id: z.string().min(1),
  sk: z.string().min(1),
  event_id: z.string().min(1),
  event_source: EventTypeSchema,
  timestamp: z.string().datetime({ offset: true }),
  received_at: z.string().datetime({ offset: true }),
  severity: SeveritySchema.optional(),
  /** Set when the envelope's claimed identity did not match the API-key-resolved record. */
  identity_mismatch: z.boolean().optional(),
  payload: z.unknown(),
});
export type EventRecord = z.infer<typeof EventRecordSchema>;

export const EventDailyCountRecordSchema = z.object({
  /** `${environment_id}#${event_source}` */
  pk: z.string().min(1),
  /** `YYYY-MM-DD` */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  count: z.number().int().nonnegative(),
});
export type EventDailyCountRecord = z.infer<typeof EventDailyCountRecordSchema>;

export const AlarmCaseStateSchema = z.enum(["OPEN", "RESOLVED"]);
export type AlarmCaseState = z.infer<typeof AlarmCaseStateSchema>;

/**
 * Current state per alarm ARN. Keeps a flapping alarm to one ticket, and
 * carries the sparse `open_critical-index` that backs the Focus page's
 * cross-customer worklist (ADR-0001 amendment). Backend-neutral by
 * design: `external_ticket_ref` is opaque (ADR-0005).
 */
export const AlarmCaseIndexRecordSchema = z.object({
  environment_id: z.string().min(1),
  alarm_arn: z.string().min(1),
  alarm_name: z.string().min(1),
  state: AlarmCaseStateSchema,
  severity: SeveritySchema,
  last_alarm_state: AlarmStateSchema,
  /** Present only while an open critical alarm exists; GSI partition key. */
  open_critical: z.literal("OPEN").optional(),
  opened_at: z.string().datetime({ offset: true }),
  last_seen_at: z.string().datetime({ offset: true }),
  resolved_at: z.string().datetime({ offset: true }).optional(),
  occurrence_count: z.number().int().positive(),
  /** SK of the `Events` item that opened this cycle. */
  open_event_sk: z.string().min(1),
  /** SK of the last `Events` item applied; a stream redelivery of the same item is a no-op. */
  last_event_sk: z.string().min(1),
  destination: EventDestinationSchema,
  external_ticket_ref: z.string().optional(),
  /**
   * #525: what a person calls the ticket in its backend (Connect's case
   * reference number), for display only - never a key; `external_ticket_ref`
   * stays the handle. Absent on rows written before it, or when the backend
   * could not be asked.
   */
  external_ticket_display: z.string().optional(),
});
export type AlarmCaseIndexRecord = z.infer<typeof AlarmCaseIndexRecordSchema>;

export const AuditActionSchema = z.enum([
  "environment.onboarded",
  "environment.offboarded",
  "environment.api_key_revoked",
  "environment.api_key_rotated",
  "environment.customer_profile_deleted",
  /** #366: offboarding's data step - the environment's rows deleted from every table keyed on it; AuditLog is never touched. */
  "environment.data_purged",
  /** #397: the handover letter was written for the organization's managers (names only in the row, never the link). */
  "environment.handover_letter_written",
  /** #397: this environment's central data exported for its owner; the row names the object, never the link. */
  "environment.data_exported",
  /** #397: terminated with --no-export - the customer's copy was declined on purpose. */
  "environment.export_waived",
  /** #397: a fresh time-limited download link issued for an existing export. */
  "environment.export_link_issued",
  /** #397: someone followed a download link and was sent the export. */
  "environment.export_downloaded",
  "environment.status_changed",
  "environment.live_changed",
  "environment.approval_requirement_changed",
  "organisation.created",
  "organisation.chain_reordered",
  /** #334: a Gairm staff member renamed the organization from Platform. */
  "organisation.renamed",
  "membership.granted",
  "membership.role_changed",
  "membership.revoked",
  "membership.profiles_changed",
  "role.created",
  "role.updated",
  "role.deleted",
  "group.created",
  "group.updated",
  "group.deleted",
  "staff.role_changed",
  "staff.exclusion_changed",
  /** ADR-0017 section 5: a route refused for want of a permission - the security signal requirements 11 asks for, written by the router. */
  "access.denied",
  "profile.created",
  "profile.published",
  "binding.created",
  "binding.removed",
  /** #481: a binding change in a gated environment, waiting for approval (decided as `binding.created` / `binding.removed` with the approver's authority, or `binding.rejected`). */
  "binding.requested",
  "binding.rejected",
  "reference_alias.set",
  "reference_alias.removed",
  "promotion.requested",
  "promotion.applied",
  "promotion.rejected",
  "override.set",
  "override.cleared",
  "rollback.applied",
  "quota_increase.requested",
  "journey.inspected",
  /** ADR-0016: a person read one hour of the customer's agent events to see the agents at a minute - names rendered, never stored. */
  "rewind.agents_viewed",
  "journey.searched",
  /** ADR-0023: the portal asked the customer's stack to check AWS Support now - a read, through their opt-in role. */
  "support_cases.checked",
  "portal_user.created",
  "portal_user.invited",
  "portal_user.disabled",
  "portal_user.enabled",
  "portal_user.removed",
  "portal_user.left",
  "portal_user.deleted",
  /** #591: a manager (or Gairm, as backstop) reset a person's authenticator, or resent their invitation. */
  "portal_user.authenticator_reset",
  "portal_user.invitation_resent",
  /** #591: a person who could not sign in asked for help and proved their mailbox. */
  "sign_in_help.requested",
  "user.preferences_changed",
  "user.profile_changed",
  /** #182: a person asked to move their account to a new email address; it changes only when that address confirms. */
  "user.email_change_requested",
  "user.email_change_canceled",
  "user.email_changed",
  /** #580: the old address undid a change of email from its "This wasn't me" link. */
  "user.email_change_reverted",
  /** R4/R11: a person signed every session out - the stamp, their remembered devices forgotten, Cognito's global sign-out. */
  "user.signed_out_everywhere",
  "security.reported",
  "identity_provider.registered",
  "email.sent",
  /** #568: customer Slack - Add to Slack started, a channel connected (or reconnected), Slack's screen canceled or the exchange failed. */
  "slack.connect_started",
  "slack.connected",
  "slack.connect_canceled",
  "slack.connect_failed",
  "slack.test_sent",
  "slack.test_failed",
  "slack.disconnected",
  /** #568: Slack answered 404/410 for a channel - written once, by whichever call flipped it to broken. */
  "slack.broken",
  /** #568: an environment's routing changed, with before and after; a first channel's defaults too. */
  "slack.routes_changed",
]);
export type AuditAction = z.infer<typeof AuditActionSchema>;

/**
 * How an action was permitted (requirements.md section 11.2). Never
 * blank: "nobody approved this" and "no approval was needed here" are
 * different sentences and the log must not leave which one ambiguous.
 */
export const AuditAuthoritySchema = z.enum(["not_required", "approved", "self_approved", "break_glass"]);
export type AuditAuthority = z.infer<typeof AuditAuthoritySchema>;

/**
 * `attempted` and `failed` are the email chain's pair (#565, crmarr/templates
 * email-ses): an external effect is recorded before it is made, then its
 * outcome. Only `email.sent` uses them; the rollup counts no email row.
 */
export const AuditResultSchema = z.enum(["applied", "rejected", "denied", "attempted", "failed"]);
export type AuditResult = z.infer<typeof AuditResultSchema>;

/** One field's before and after. Section 11.6 replays `before` to roll back. */
export const AuditFieldChangeSchema = z.object({
  /** `${type}/${name}`, e.g. `announcement/hold_1`. */
  field: z.string().min(1),
  before: z.unknown().optional(),
  after: z.unknown().optional(),
  /** True where a promotion left the target's own value in place. */
  kept_target_value: z.boolean().optional(),
});
export type AuditFieldChange = z.infer<typeof AuditFieldChangeSchema>;

/**
 * `AuditLog` - PK `organisation_id` (ADR-0009), SK `${timestamp}#${action_id}`.
 * Keyed on the organization because some actions - reordering the chain,
 * granting a role - belong to no single environment. Append-only,
 * enforced in IAM rather than by convention (requirements.md 11.4).
 */
export const AuditLogRecordSchema = z.object({
  organisation_id: z.string().min(1),
  /** `${timestamp}#${action_id}` */
  sk: z.string().min(1),
  action: AuditActionSchema,
  /** Present where the action belongs to one environment. */
  environment_id: z.string().optional(),
  /** IAM principal ARN or Cognito subject that performed the action. */
  actor: z.string().min(1),
  /**
   * The Cognito subject behind `actor`, which is usually an email. Kept
   * separately because an email can change and a subject cannot, and
   * because section 12 resolves activity back to a user row by it.
   */
  actor_subject: z.string().optional(),
  /** The actor's display name at the time (section 13.2), for "Name (email)" in the Audit tab; the email remains `actor`. */
  actor_name: z.string().optional(),
  /** Whether the actor acted as the customer's own user or as Gairm staff. */
  actor_is_internal: z.boolean(),
  /** The actor was Gairm staff working inside the customer's own portal view (ADR-0011). */
  view_as: z.literal(true).optional(),
  authority: AuditAuthoritySchema,
  /** Cognito subject of the approving manager, where `authority` is `approved`. */
  approved_by: z.string().optional(),
  result: AuditResultSchema,
  changes: z.array(AuditFieldChangeSchema).optional(),
  /** Set on `rollback.applied`: the SK of the entry whose values were replayed. */
  rolled_back_from: z.string().optional(),
  /** False where the action cannot be undone - a revoked key, a submitted quota request. */
  reversible: z.boolean(),
  timestamp: z.string().datetime({ offset: true }),
  request_id: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
  /** Epoch seconds; 12 months per CLAUDE.md section 17. */
  ttl: z.number().int().positive(),
});
export type AuditLogRecord = z.infer<typeof AuditLogRecordSchema>;

export const SupportCaseRecordSchema = z.object({
  environment_id: z.string().min(1),
  case_id: z.string().min(1),
  display_id: z.string().optional(),
  subject: z.string(),
  status: z.string(),
  severity: z.string().optional(),
  service: z.string().optional(),
  time_created: z.string().optional(),
  last_updated: z.string(),
  last_communications: z.array(
    z.object({ body: z.string(), submitted_by: z.string().optional(), time_created: z.string() }),
  ),
  observed_at: z.string().datetime({ offset: true }),
});
export type SupportCaseRecord = z.infer<typeof SupportCaseRecordSchema>;

export const ServiceQuotaSnapshotRecordSchema = z.object({
  environment_id: z.string().min(1),
  /** `${service_code}#${quota_code}`, or `${service_code}#${quota_code}#${resource_arn}` for a per-instance value (#495). */
  sk: z.string().min(1),
  service_code: z.string().min(1),
  quota_code: z.string().min(1),
  quota_name: z.string().min(1),
  value: z.number(),
  unit: z.string().optional(),
  adjustable: z.boolean(),
  usage: z.number().nonnegative().optional(),
  usage_percent: z.number().nonnegative().optional(),
  usage_source: QuotaUsageSourceSchema,
  /** #495: per account or per resource; absent on a row from a stack that read account-level values only. */
  scope: QuotaScopeSchema.optional(),
  resource_type: z.string().optional(),
  /** The environment's instance, for a per-instance value. */
  resource_arn: z.string().optional(),
  /** `resource`: the instance's own value; `account`: the account's default for each instance. */
  applied_at_level: QuotaScopeSchema.optional(),
  /** Sparse GSI key (`near_limit-index`): present only at >= 80% of the limit, for the Focus page's cross-customer worklist. */
  near_limit: z.literal("YES").optional(),
  observed_at: z.string().datetime({ offset: true }),
});
export type ServiceQuotaSnapshotRecord = z.infer<typeof ServiceQuotaSnapshotRecordSchema>;

/** `MetricSeries` — ADR-0004 concurrency points. SK `${metric}#${timestamp}`, 90-day TTL. */
export const MetricPointRecordSchema = z.object({
  environment_id: z.string().min(1),
  sk: z.string().min(1),
  metric: ConcurrencyMetricSchema,
  timestamp: z.string().datetime({ offset: true }),
  value: z.number().nonnegative(),
  ttl: z.number().int().positive(),
});
export type MetricPointRecord = z.infer<typeof MetricPointRecordSchema>;

/** `JourneyPathRollups` — ADR-0003 Track A. SK `${windowStart}#${dimension}#${slice}`. */
export const JourneyRollupRecordSchema = z.object({
  environment_id: z.string().min(1),
  sk: z.string().min(1),
  window_start: z.string().datetime({ offset: true }),
  window_end: z.string().datetime({ offset: true }),
  dimension: JourneyDimensionSchema,
  slice: z.string().min(1),
  contact_count: z.number().int().nonnegative(),
  edges: z.array(JourneyEdgeSchema),
  observed_at: z.string().datetime({ offset: true }),
  /** #457: the aggregator run that wrote the row; a window manifest removes only rows from an earlier run. Absent on rows from older templates. */
  run_id: z.string().optional(),
  run_started_at: z.string().datetime({ offset: true }).optional(),
  ttl: z.number().int().positive(),
});
export type JourneyRollupRecord = z.infer<typeof JourneyRollupRecordSchema>;

/**
 * `Contacts` — ADR-0015 section 8. One row per contact, always-replace by
 * contact id; the payload is the allow-list, and the row adds only what
 * central needs to key on it. GSI `by-initiation` (PK `environment_id`,
 * SK `initiation_timestamp`) for time-ordered lists; GSI `by-caller`
 * (PK `environment_id#caller_hash`, SK `initiation_timestamp`) for
 * repeats and search by number.
 */
export const ContactRecordSchema = ContactRecordPayloadSchema.extend({
  environment_id: z.string().min(1),
  /** `${environment_id}#${caller_hash}`, present only when the record carried a caller hash - the sparse `by-caller` key. */
  caller_key: z.string().optional(),
  /**
   * ADR-0016 (#231): the sparse `by-queue-enqueue` key, `${environment_id}#${queue name}`, and its sort key and the
   * dequeue time copied out of `queue` - present only on a record that queued. Rewind's cross-check.
   */
  queue_key: z.string().optional(),
  queue_enqueue_timestamp: z.string().datetime({ offset: true }).optional(),
  queue_dequeue_timestamp: z.string().datetime({ offset: true }).optional(),
  /** #349: the voice the contact heard last, from its flow logs (a `contact_voice` push). Absent: the instance default, or not read yet. A re-emitted record keeps it. */
  voice: z.string().max(64).optional(),
  engine: z.string().max(32).optional(),
  received_at: z.string().datetime({ offset: true }),
  ttl: z.number().int().positive(),
});
export type ContactRecord = z.infer<typeof ContactRecordSchema>;


/**
 * `ContactDailyCounts` — PK `environment_id`, SK `date` (UTC day of the
 * initiation). Populated by a DynamoDB Streams trigger off `Contacts`,
 * exactly-once via `_seen#` marker rows (TTL 48h) in the same table, the
 * design `EventDailyCounts` uses. Backs the Journeys overview with bounded
 * reads: 90 rows for 90 days, whatever the contact volume. Channel counts
 * ride the day item as `channel_<CHANNEL>` attributes; the seconds fields
 * are sums, divided at read time.
 */
export const ContactDailyCountRecordSchema = z.object({
  environment_id: z.string().min(1),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  contacts: z.number().int().nonnegative().default(0),
  /** #462: the three outcomes (contact-outcome.ts); every contact is in exactly one. */
  contained: z.number().int().nonnegative().default(0),
  not_contained: z.number().int().nonnegative().default(0),
  escalated: z.number().int().nonnegative().default(0),
  /** The detail beneath `escalated`: answered by an agent, transferred out, abandoned in queue. */
  answered: z.number().int().nonnegative().default(0),
  transferred_out: z.number().int().nonnegative().default(0),
  abandoned: z.number().int().nonnegative().default(0),
  /** The same caller (by hash) within the seven days before - counted on arrival, never revised. */
  repeat_7d: z.number().int().nonnegative().default(0),
  /** Queue seconds of answered contacts and of abandoned ones, kept apart: the two averages mean different things. */
  answered_wait_seconds: z.number().int().nonnegative().default(0),
  abandoned_wait_seconds: z.number().int().nonnegative().default(0),
  handle_seconds: z.number().int().nonnegative().default(0),
  /** Contacts whose record carried a poor audio quality score on either side. */
  poor_audio: z.number().int().nonnegative().default(0),
}).catchall(z.number().int().nonnegative());
export type ContactDailyCountRecord = z.infer<typeof ContactDailyCountRecordSchema>;

/** `QuotaIncreaseRequests` — ADR-0002, #464. SK `${service_code}#${quota_code}#${requested_at}`; one row per AWS request id. */
export const QuotaIncreaseStatusSchema = z.enum(["PENDING", "CASE_OPENED", "APPROVED", "DENIED", "NOT_APPROVED", "CASE_CLOSED", "INVALID_REQUEST", "UNKNOWN"]);
export type QuotaIncreaseStatus = z.infer<typeof QuotaIncreaseStatusSchema>;

/**
 * #464: `starsach` is a request filed from the portal (ADR-0002), with its
 * requester and audit trail; `outside` is one the stack's quota poller found
 * in the account's history (the console, the CLI), which has neither. A row
 * written before the field existed is Starsach's.
 */
export const QuotaRequestSourceSchema = z.enum(["starsach", "outside"]);
export type QuotaRequestSource = z.infer<typeof QuotaRequestSourceSchema>;

export const QuotaIncreaseRequestRecordSchema = z.object({
  environment_id: z.string().min(1),
  sk: z.string().min(1),
  service_code: z.string().min(1),
  quota_code: z.string().min(1),
  quota_name: z.string().min(1),
  requested_value: z.number().positive(),
  previous_value: z.number().optional(),
  aws_request_id: z.string().min(1),
  status: QuotaIncreaseStatusSchema,
  source: QuotaRequestSourceSchema.default("starsach"),
  /** Who filed it from the portal; absent on a request made outside Starsach. */
  requested_by: z.string().min(1).optional(),
  requested_at: z.string().datetime({ offset: true }),
  last_checked_at: z.string().datetime({ offset: true }),
  case_id: z.string().optional(),
  /** #495: the instance a per-instance request was for (AWS's `ContextId`); absent for an account-level request. */
  resource_arn: z.string().optional(),
});
export type QuotaIncreaseRequestRecord = z.infer<typeof QuotaIncreaseRequestRecordSchema>;

/**
 * `<stack>-journey-cache` — ADR-0003 Track B, relocated by ADR-0006. The
 * assembled view is CX content (transcripts, caller numbers, CRM
 * payloads), so this table lives in the **customer's** account, created
 * by their template, with their retention; its name reaches central in
 * the heartbeat (`cross_account.journey_cache_table`).
 */
export const CallJourneyStatusSchema = z.enum(["assembling", "complete", "failed"]);
export type CallJourneyStatus = z.infer<typeof CallJourneyStatusSchema>;

export const CallJourneyCacheRecordSchema = z.object({
  environment_id: z.string().min(1),
  contact_id: z.string().min(1),
  status: CallJourneyStatusSchema,
  started_at: z.string().datetime({ offset: true }),
  assembled_at: z.string().datetime({ offset: true }).optional(),
  requested_by: z.string().min(1),
  /** Partial while assembling: panels fill in as each source completes (design.md "Journeys module"). */
  journey: z.unknown(),
  error: z.string().optional(),
  ttl: z.number().int().positive(),
});
export type CallJourneyCacheRecord = z.infer<typeof CallJourneyCacheRecordSchema>;

/**
 * `UsageMonthly` - PK `environment_id`, SK `month` (#272). What the
 * customer's stack metered for the month, always-replace; the classified
 * figures are central's reading of the lines, and the price rule is
 * applied at read time, never stored.
 */
export const UsageMonthlyRecordSchema = z.object({
  environment_id: z.string().min(1),
  month: z.string().regex(/^\d{4}-\d{2}$/),
  aws_account_number: z.string().regex(/^\d{12}$/),
  source: z.enum(["cost-explorer", "connect-metrics"]),
  lines: z.array(z.object({ usage_type: z.string(), quantity: z.number(), unit: z.string().optional() })),
  voice_minutes: z.number().nonnegative(),
  chat_messages: z.number().nonnegative(),
  emails: z.number().nonnegative(),
  cases: z.number().nonnegative(),
  /** Web/in-app calling minutes (#493): metered, not priced - the call's voice minute is already in `voice_minutes`. Absent on rows written before #493. */
  in_app_minutes: z.number().nonnegative().optional(),
  /** Lines of a Connect usage type the classifier does not know (#493): never priced, never dropped, shown on the platform view. */
  unclassified: z.array(z.object({ usage_type: z.string(), quantity: z.number(), unit: z.string().optional() })).optional(),
  as_of: z.string().datetime({ offset: true }),
  complete: z.boolean(),
  received_at: z.string().datetime({ offset: true }),
});
export type UsageMonthlyRecord = z.infer<typeof UsageMonthlyRecordSchema>;

/**
 * `EmailSuppressions` - PK `address_key`, no SK (CLAUDE.md section 13; the
 * crmarr/templates email-ses shape). One row per address that bounced or
 * complained, keyed by an HMAC of the lower-cased address under the
 * environment's `starsach/<env>/email-suppression-key`: no plaintext
 * address anywhere. Written only by the environment's SES events consumer;
 * read before every send, and before every Cognito invitation (#487).
 */
export const EmailSuppressionReasonSchema = z.enum(["complaint", "hard_bounce", "soft_bounce"]);
export type EmailSuppressionReason = z.infer<typeof EmailSuppressionReasonSchema>;
export const EmailSuppressionRecordSchema = z.object({
  address_key: z.string().regex(/^[0-9a-f]{64}$/),
  reason: EmailSuppressionReasonSchema,
  /** The SES event type and subtype, and the configuration set. */
  source: z.string().optional(),
  first_event_at: z.string().datetime({ offset: true }),
  last_event_at: z.string().datetime({ offset: true }),
  /** Epoch seconds; absent for a complaint, which never expires. */
  expires_at: z.number().int().positive().optional(),
});
export type EmailSuppressionRecord = z.infer<typeof EmailSuppressionRecordSchema>;
